'use client';

/**
 * Snabbfakta på produktkortet. Ägaren 2026-09-26: "om man hoovrar över ngn så
 * ska man få snabb fakta över dess dimensioner o position o allt om den men i
 * mobil kanske det väsentligaste i badge på dem sen en lite knapp man kan
 * trycka info".
 *
 * - Monteringsbadges (Fram/Bak/Vänster/Höger …) syns alltid — det är det man
 *   oftast väljer fel på.
 * - Dator: hovra på kortet → faktarutan fälls ut över bilden.
 * - Mobil: ℹ️-knappen i bildens hörn öppnar samma ruta.
 */
import { Info, X } from 'lucide-react';

const LAGEN: Array<[RegExp, string, string]> = [
  [/\bfram(axel)?\b|\bfrämre\b/i, 'Fram', 'bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-200'],
  [/\bbak(axel)?\b|\bbakre\b/i, 'Bak', 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200'],
  [/\bvänster\b/i, 'Vänster', 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200'],
  [/\bhöger\b/i, 'Höger', 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200'],
  [/\bövre\b/i, 'Övre', 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200'],
  [/\bnedre\b/i, 'Nedre', 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200'],
  [/\binre\b/i, 'Inre', 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200'],
  [/\byttre\b/i, 'Yttre', 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200'],
];

/** Monteringsläge ur Position-attributet och namnet. */
export function monteringslagen(namn: string, kf?: Record<string, string> | null) {
  const text = `${namn} ${kf?.['Position'] ?? ''} ${kf?.['Delens position'] ?? ''} ${kf?.['Monteringssida'] ?? ''}`;
  return LAGEN.filter(([re]) => re.test(text)).map(([, etikett, klass]) => ({ etikett, klass }));
}

export function MonteringsBadges({ namn, kf }: { namn: string; kf?: Record<string, string> | null }) {
  const lagen = monteringslagen(namn, kf);
  if (!lagen.length) return null;
  return (
    <div className="mt-1 flex flex-wrap gap-1">
      {lagen.map((l) => (
        <span key={l.etikett} className={`rounded px-1.5 py-px text-[10px] font-semibold leading-4 ${l.klass}`}>{l.etikett}</span>
      ))}
    </div>
  );
}

/** "Ytterdiameter [mm]" = "280" → ["Ytterdiameter", "280 mm"]. */
function rad(namn: string, varde: string): [string, string] {
  const m = namn.match(/^(.*?)\s*[[(]\s*([^\])]+?)\.?\s*[\])]\s*$/);
  if (m) return [m[1]!, `${varde} ${m[2]!.replace(/^mm$/, 'mm')}`];
  return [namn, varde];
}

export function SnabbfaktaKnapp({ oppen, onToggle, etikett }: { oppen: boolean; onToggle: () => void; etikett: string }) {
  return (
    <button
      type="button"
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggle(); }}
      aria-expanded={oppen}
      aria-label={etikett}
      className="absolute top-1.5 right-1.5 z-10 h-9 w-9 rounded-full bg-white border border-border/70 flex items-center justify-center hover:border-brand/30 transition-colors"
    >
      <Info className="h-4 w-4 text-muted-foreground" />
    </button>
  );
}

export function SnabbfaktaPanel({
  namn, kf, referens, marke, passar, onClose, sv,
}: {
  namn: string; kf?: Record<string, string> | null; referens?: string | null; marke?: string | null;
  passar?: string | null; onClose: () => void; sv: boolean;
}) {
  // Samma brusfilter som partner-API:ts `fakta`: positionen syns redan som etikett,
  // och 0.001 kg är en platshållarvikt från katalogen.
  const fakta = Object.entries(kf ?? {}).map(([k, v]) => rad(k, v))
    .filter(([k, v]) => !/^(position|delens position|specifikation|monteringssida)$/i.test(k)
      && !(/^vikt$/i.test(k) && parseFloat(v.replace(',', '.')) < 0.01));
  return (
    <div
      role="dialog"
      aria-label={sv ? 'Snabbfakta' : 'Quick facts'}
      className="absolute inset-x-0 top-0 z-20 aspect-square overflow-y-auto overflow-x-hidden border-b bg-background/97 p-2.5 sm:p-3 text-xs shadow-lg backdrop-blur-sm"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="mb-1.5 flex items-start justify-between gap-2">
        <p className="font-semibold leading-tight line-clamp-2">{namn}</p>
        <button type="button" onClick={(e) => { e.preventDefault(); onClose(); }}
          aria-label={sv ? 'Stäng' : 'Close'} className="-mr-1 -mt-1 rounded p-1 text-muted-foreground hover:bg-muted">
          <X className="h-4 w-4" />
        </button>
      </div>
      <MonteringsBadges namn={namn} kf={kf} />
      <dl className="mt-2 grid grid-cols-1 gap-y-0 sm:grid-cols-[auto_minmax(0,1fr)] sm:gap-x-2 sm:gap-y-1 [&_dd]:min-w-0 [&_dd]:break-words [&_dd]:mb-1 sm:[&_dd]:mb-0 [&_dt]:text-[10px] sm:[&_dt]:text-xs">
        {marke && (<><dt className="text-muted-foreground">{sv ? 'Märke' : 'Brand'}</dt><dd className="font-medium">{marke}</dd></>)}
        {referens && (<><dt className="text-muted-foreground">{sv ? 'Art.nr' : 'Part no.'}</dt><dd className="font-mono">{referens}</dd></>)}
        {fakta.map(([k, v]) => (
          <div key={k} className="contents"><dt className="text-muted-foreground">{k}</dt><dd className="font-medium tabular-nums">{v}</dd></div>
        ))}
      </dl>
      {passar && <p className="mt-2 text-muted-foreground">{passar}</p>}
      {!fakta.length && <p className="mt-2 text-muted-foreground">{sv ? 'Fler uppgifter finns på produktsidan.' : 'More details on the product page.'}</p>}
    </div>
  );
}
